import React, { useState } from 'react';
import { FileText, Download, AlertCircle, LogIn } from 'lucide-react';
import { getAuthHeader } from '../../services/otpAuthService';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface Documento {
  _id: string;
  tipo: string;
  filename: string;
  originalName: string;
  size: number;
  uploadDate: string;
  mimetype: string;
}

interface DocumentiVisualizzazioneProps {
  attoId: string;
  documenti: Documento[];
  isRichiedente?: boolean;
}

const DocumentiVisualizzazione: React.FC<DocumentiVisualizzazioneProps> = ({ attoId, documenti, isRichiedente = false }) => {
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  const tipoLabels: Record<string, string> = {
    cartaCircolazione: 'Carta di Circolazione',
    docIdentitaVenditore: 'Documento Identità Venditore',
    docIdentitaAcquirente: 'Documento Identità Acquirente'
  };

  const handleDownload = async (docId: string, originalName: string) => {
    const authHeader = getAuthHeader() as { Authorization?: string };
    const token = authHeader.Authorization?.replace('Bearer ', '') || '';

    try {
      // Scarica il documento con axios
      const response = await axios.get(
        `${API_BASE_URL}/api/atti-svincolo/${attoId}/documenti/${docId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Crea un URL temporaneo per il blob e scarica il file
      const blob = new Blob([response.data], { type: String(response.headers['content-type'] || 'application/octet-stream') });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token scaduto o non valido - mostra modal
        setShowLoginModal(true);
      } else {
        console.error('Errore download documento:', error);
        alert('Errore durante il download del documento');
      }
    }
  };

  const handleGoToLogin = () => {
    // Salva l'URL corrente per tornare dopo il login
    localStorage.setItem('redirectAfterLogin', window.location.pathname);
    navigate('/area-riservata/login');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (!documenti || documenti.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <FileText className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
          Documenti
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">Nessun documento disponibile</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2.5 flex items-center gap-2">
        <FileText className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
        Documenti ({documenti.length})
      </h2>

      <div className="space-y-1.5">
        {documenti.map((doc) => (
          <button
            key={doc._id}
            onClick={() => handleDownload(doc._id, doc.originalName)}
            className="w-full text-left p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/10 hover:border-purple-300 dark:hover:border-purple-700 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-900 dark:text-white">
                  {tipoLabels[doc.tipo] || doc.tipo}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={doc.originalName}>
                  {doc.originalName}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {formatDate(doc.uploadDate)} &middot; {formatSize(doc.size)}
                </p>
              </div>
              <Download className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 group-hover:text-purple-500 transition-colors flex-shrink-0 ml-2" />
            </div>
          </button>
        ))}
      </div>

      <p className="mt-2.5 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2.5 py-1.5">
        I documenti saranno disponibili per 48 ore dalla data dell'appuntamento.
      </p>

      {showLoginModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full p-5">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Sessione Scaduta</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Effettua nuovamente il login per scaricare i documenti.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowLoginModal(false)}
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={handleGoToLogin}
                    className="flex-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5"
                  >
                    <LogIn className="w-3.5 h-3.5" />
                    Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentiVisualizzazione;

